<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    :fullWhiteBox="isInitialAction"
    :nonAuthenticatedFeature="isInitialAction"
    :hideClose="isInitialAction"
    :hideWhiteBoxOnLoad="showLoader || applyErrorState"
    :customBreadcrumbs="customBreadcrumbs"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :footerButtonsParams="buttonsFooterData"
    :showFooter="isInitialAction"
    @backOrCloseClicked="navigateByName(routeList.USER_AREA_DEFINITIONS_NAME)">
        <association-information
        v-if="!showLoader"
        :class="{'prevent-auto-margins': isInitialAction}"
        :key="deviceInformation"
        :isInitialAction="isInitialAction"
        :deviceInformation="deviceInformation"
        @deactivateDevice="deactivateDeviceHandler"
        @activateDevice="activateDeviceHandler"/>
    </wrapper-features-templates-one-column>
    <!-- Confirmation Modal Alert -->
    <modal-centered-generic
    v-if="showConfirmationModal"
    :titleIcon="confirmationModalData.negative ? null : 'info'"
    :negative="confirmationModalData.negative"
    :rightButtonIcon="confirmationModalData.rightButtonIcon"
    :title="confirmationModalData.title"
    :text="confirmationModalData.text"
    :leftButtonText="confirmationModalData.leftButtonText"
    :rightButtonText="confirmationModalData.rightButtonText"
    @hideMe="showConfirmationModal = false"
    @rightButtonClicked="confirmationModalData.function"/>
    <!-- Validate Code Modal -->
    <validate-code-modal
    v-if="showValidateCodeModal"
    :enrollmentOtpTemplate="enrollmentOtpTemplate"
    @requestNewToken="requestNewTokenHandler"
    @validateCode="validateCodeHandler"
    @hideMe="showValidateCodeModal = false"/>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ValidateCodeModal from '@/components/features/userArea/definitions/secureAssociationEnrollment/ValidateCodeModal'
import AssociationInformation from '@/components/features/userArea/definitions/secureAssociationEnrollment/AssociationInformation'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { useRoute } from 'vue-router'
import { ref, computed } from 'vue'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByPath, navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'updateLoginLoader'])

emit('updateLoginLoader', false)

/* mapActions && mapGetters */
const {
    consultSecureAssociation,
    deleteSecureAssociation,
    initiateEnrollmentProcess,
    retryInitiateEnrollmentProcess,
    confirmEnrollmentProcess
} = mapActions(moduleMapper.USER_AREA_DEFINITIONS_SECURE_ASSOCIATION_ENROLLMENT)
const { updateEntityRememberInfo } = mapActions(moduleMapper.SESSION)
const { getRememberMeEntityInfo, getMobileBiometric } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

const currentRoute = useRoute()

const isInitialAction = computed(() => {
	return currentRoute.path.includes('initialSecureAssociationEnrollment')
})

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showConfirmationModal = ref(false)
const confirmationModalData = ref({})
const temporaryDeviceInfo = ref(null)
const deviceInformation = ref(null)
const showValidateCodeModal = ref(false)
const processData = ref(null)
const validationCode = ref(null)
const enrollmentOtpTemplate = ref(null)

const biometricType = computed(() => { return getMobileBiometric.value === 'face' ? t('modals.token.useIosFace') : t('modals.token.biometricButton') })

const rememberMeSelectedAcession = computed(() => {
	if (!getRememberMeEntityInfo.value) return null
	return getRememberMeEntityInfo.value?.listOfAcessions.find(el => el.selected) ?? getRememberMeEntityInfo.value.listOfAcessions[0]
})

const rememberMeToken = computed(() => {
	return rememberMeSelectedAcession.value.rememberMeToken
})

const customBreadcrumbs = computed(() => {
    return isInitialAction.value ? {
        currentArea: t('userArea.definitions.secureAssociationEnrollment.breadcrumbs')
    } : null
})

const buttonsFooterData = computed(() => {
    return [{
        label: t('userArea.definitions.secureAssociationEnrollment.ignoreActivation'),
        buttonType: 'tertiary',
        callbackFunction: () => { ignoreActivation() }
    }]
})

const ignoreActivation = () => {
    confirmationModalData.value = {
        negative: false,
        rightButtonIcon: 'arrow-right-full',
        title: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.ignoreActivation'),
        text: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.ignoreActivationMessage'),
        leftButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.no'),
        rightButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.ignore'),
        function: () => {
            !getMobileAppState.value ? navigateByPath(routeList.HOME_PATH) : navigateByPath(routeList.HOME_MOBILE_PATH)
            applyHasSeenSecureAssociationInitialActionFlagOnAccession()
        }
    }

    showConfirmationModal.value = true
}

const activateDeviceHandler = event => {
    confirmationModalData.value = {
        negative: false,
        rightButtonIcon: 'arrow-right-full',
        title: event.deviceInfo.modalTitle,
        text: event.deviceInfo.modalText,
        leftButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.no'),
        rightButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.continue'),
        function: () => { initiateAssociationProcess(event.deviceInfo) }
    }

    showConfirmationModal.value = true
}

const deactivateDeviceHandler = event => {
    confirmationModalData.value = {
        negative: true,
        rightButtonIcon: 'check-line',
        title: event.deviceInfo.modalTitle,
        text: event.deviceInfo.modalText,
        leftButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.no'),
        rightButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivate'),
        function: () => { deleteAssociationHandler() }

    }

    showConfirmationModal.value = true
}

const validateCurrentDevice = () => {
    if (deviceInformation.value.clientChannelDevice.id === window.device.uuid) {
        deleteAssociationHandler()
    } else showLoader.value = false
}

/*************************************************
*                                                *
*                   Biometric                    *
*                                                *
*************************************************/
const validateCodeHandler = event => {
    confirmationModalData.value = {
        negative: false,
        rightButtonIcon: 'check-line',
        title: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.authorizeBiometricsTitle'),
        text: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.authorizeBiometricsText', { biometricType: biometricType.value }),
        leftButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.no'),
        rightButtonText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.continue'),
        function: () => { showBiometricDialog(event) }

    }

    showConfirmationModal.value = true
}

const keyPair = ref(null)

const showBiometricDialog = async event => {
	showOverlayLoader.value = true
    showConfirmationModal.value = false
    validationCode.value = event

	keyPair.value = await crypto.subtle.generateKey({
			name: 'RSA-OAEP',
			modulusLength: 2048,
			publicExponent: new Uint8Array([1, 0, 1]), // 24 bit representation of 65537
			hash: 'SHA-1'
		},
		true,
		['encrypt', 'decrypt']
	)

	var exportedPrivateKey = await crypto.subtle.exportKey('jwk', keyPair.value.privateKey)

	if (window.cordova && window.cordova.platformId === 'ios') {
		window.Fingerprint.show({
			title: t('biometricLogin.title'),
			description: t('biometricLogin.description'),
			cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
			disableBackup: true
		}, (a) => {
			window.Fingerprint.registerBiometricSecret({
                secretKey: 'associationKey',
				secret: JSON.stringify(exportedPrivateKey),
				title: t('biometricLogin.title'),
				description: t('biometricLogin.description'),
				cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
				disableBackup: true
			}, confirmProcessHandler, showBiometricDialogError)
		}, showBiometricDialogError)
	} else {
		window.Fingerprint.registerBiometricSecret({
            secretKey: 'associationKey',
			secret: JSON.stringify(exportedPrivateKey),
			title: t('biometricLogin.title'),
			description: t('biometricLogin.description'),
			cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
			disableBackup: true
		}, confirmProcessHandler, showBiometricDialogError)
	}
}

/* When the user rejects biometric login we proceed to the user+pass one */
const showBiometricDialogError = error => {
	if (error.code !== -108 && error.code !== -109) {
		feedbackToastData.value.text = error && error.message ? error.message : t('genericErrorMessage')
		if (error.code === -102) feedbackToastData.value.text = t('biometricLogin.authenticationFailed')
		showFeedbackToast.value = true
		feedbackToastData.value.toastType = 'error'
	}
	showOverlayLoader.value = false
}

const arrayBufferToBase64String = arrayBuffer => {
	var binary = ''
	var bytes = new Uint8Array(arrayBuffer)
	var len = bytes.byteLength
	for (var i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i])
	}
	return btoa(binary)
}

/*************************************************
*                                                *
*                   Requests                     *
*                                                *
*************************************************/
const initialRequest = async () => {
    showLoader.value = true
    showFeedbackToast.value = false
    applyErrorState.value = false

    try {
        const res = await consultSecureAssociation()
        deviceInformation.value = res.data.data

        if (isInitialAction.value && getMobileAppState.value && deviceInformation.value.clientChannelDevice) {
            validateCurrentDevice()
            return
        }

        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequest)
    }
}

const deleteAssociationHandler = async () => {
    if (!showLoader.value) showOverlayLoader.value = true
    showFeedbackToast.value = false
    showConfirmationModal.value = false

    try {
        await deleteSecureAssociation()
        if (!isInitialAction.value) {
            successHandler({ message: t('userArea.definitions.secureAssociationEnrollment.deactivatedWithSuccess') })
        }

        deviceInformation.value = {
            alreadyHasActiveSecureAssociation: !deviceInformation.value.alreadyHasActiveSecureAssociation,
            clientChannelDevice: null
        }

        processData.value = null
        showOverlayLoader.value = false
        showLoader.value = false
        applyHasSeenSecureAssociationInitialActionFlagOnAccession(false)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, deleteAssociationHandler)
    }
}

const initiateAssociationProcess = async deviceInfo => {
    showFeedbackToast.value = false
	showOverlayLoader.value = true
    showConfirmationModal.value = false

    temporaryDeviceInfo.value = deviceInfo

    const payload = {
        params: { rememberMeToken: rememberMeToken.value },
        data: {
            clientChannelDevice: { id: deviceInfo.id, system: deviceInfo.system }
        }
    }

	try {
        const res = await initiateEnrollmentProcess(payload)
        processData.value = { id: res.data.data.id, deviceInfo }
        enrollmentOtpTemplate.value = res.data.data.enrollmentOtpTemplate
		showOverlayLoader.value = false
        showValidateCodeModal.value = true
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, () => { initiateAssociationProcess(deviceInfo) })
	}
}

const requestNewTokenHandler = async () => {
    showFeedbackToast.value = false
	showOverlayLoader.value = true

    const payload = {
        params: { id: processData.value.id },
        data: {
            clientChannelDevice: { id: temporaryDeviceInfo.value.id, system: temporaryDeviceInfo.value.system }
        }
    }
	try {
        const res = await retryInitiateEnrollmentProcess(payload)
		showOverlayLoader.value = false
        showValidateCodeModal.value = true
        successHandler(res.data)
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestNewTokenHandler)
	}
}

const confirmProcessHandler = async () => {
    showOverlayLoader.value = true
    showFeedbackToast.value = false

    var exportedPublicKey = await crypto.subtle.exportKey('spki', keyPair.value.publicKey)

	const publicKey = arrayBufferToBase64String(exportedPublicKey)

    const payload = {
        data: {
            clientChannelDevice: {
                id: processData.value.deviceInfo.id,
                system: processData.value.deviceInfo.system,
                systemVersion: processData.value.deviceInfo.systemVersion,
                vendor: processData.value.deviceInfo.vendor,
                model: processData.value.deviceInfo.model
            },
            id: processData.value.id,
            otp: validationCode.value,
            publicKey: publicKey
        }
    }

    try {
        const res = await confirmEnrollmentProcess(payload)
        processData.value = null
        deviceInformation.value = res.data.data
        successHandler({ message: t('userArea.definitions.secureAssociationEnrollment.activatedWithSuccess') })
        applyHasSeenSecureAssociationInitialActionFlagOnAccession('enrolledSuccessSecuredDevice')
        temporaryDeviceInfo.value = null
        showValidateCodeModal.value = false
        showOverlayLoader.value = false

        // For the case we complete association as initial action
        if (isInitialAction.value) {
            setTimeout(() => { navigateByPath(routeList.HOME_MOBILE_PATH) }, 2000)
        }
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, confirmEnrollmentProcess)
    }
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    showOverlayLoader.value = false
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequest()

const applyHasSeenSecureAssociationInitialActionFlagOnAccession = (enrolledSuccessSecuredDevice) => {
    if (!getRememberMeEntityInfo.value) return

    const entityParsed = {
        ...getRememberMeEntityInfo.value,
        listOfAcessions: getRememberMeEntityInfo.value.listOfAcessions.map(accession => {
            return {
                ...accession,
                hasSeenSecureAssociationInitialAction: rememberMeSelectedAcession.value.acessionNumber === accession.acessionNumber ? true : accession.hasSeenSecureAssociationInitialAction,
                hadSecuredAssociation: enrolledSuccessSecuredDevice === null ? accession.alreadyHasActiveSecureAssociation : enrolledSuccessSecuredDevice
            }
        })
    }

    updateEntityRememberInfo(entityParsed)
}
</script>

<style lang="scss" scoped>
</style>
